import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { AccIntegrationName } from '@constants/integrations';
import { accountActions, authActions, googleActions } from '@actions';
import { companyService } from '@services';
import GTManager from '@widgets/gtm';
import GoogleCompanyItem from './GoogleCompanyItem';
import { useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { Footer, Header, GoogleContentWrapper, GoogleFooter, Progress, ExpandedSectionWrapper } from './styles';

const ViewGoogle = ({ onAddManuallyClick, onConnectAnotherAccountClick }) => {
    const { t } = useTranslation();
    const [companies, setCompanies] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [section, setSection] = useState({});

    const account = useSelector(state => state.account?.account);
    const LOCATIONS = useSelector(state => state.google.data);
    const locationsLoading = useSelector(state => state.google.loading);
    const dispatch = useDispatch();

    const GOOGLE_CONNECTED = account?.integrations.find(i => i.type === AccIntegrationName.Google)?.isActive;

    useEffect(() => {
        if (GOOGLE_CONNECTED) {
            if (LOCATIONS.length === 0) {
                dispatch(googleActions.get());
            } else {
                setSection(
                    Object.fromEntries(
                        [...new Set(LOCATIONS.map(l => l.accountName))].map(accountName => [accountName, true]),
                    ),
                );
            }
        }
    }, [dispatch, GOOGLE_CONNECTED, LOCATIONS.length]);

    const onCheckCompany = location => {
        if (companies.some(c => c.locationId === location.locationId)) {
            setCompanies(companies.filter(c => c.locationId !== location.locationId));
            return;
        }
        setCompanies([...new Set([...companies, location])]);
    };

    const onCheckAll = () => {
        if (companies.length === LOCATIONS.length) {
            setCompanies([]);
            return;
        }
        setCompanies([...new Set([...LOCATIONS])]);
    };

    const onFinishOnboarding = async () => {
        const updatedAcc = await dispatch(accountActions.get());
        await dispatch(accountActions.updateAccount({ ...updatedAcc, onboardingCompleted: true, onboardingStatus: 5 }));
        GTManager.push('onboarding-completed');
        await dispatch(authActions.getCurrentUser());
        dispatch(googleActions.reset());
    };

    const onComplete = async () => {
        setIsSubmitting(true);
        try {
            await companyService.importGoogleCompanies(companies);
            await onFinishOnboarding();
        } catch (error) {
            handleApiError(error);
            if (error.errorCode === 'profile_connected_to_another_location') {
                await onFinishOnboarding();
            }
        }
        setIsSubmitting(false);
    };

    const companiesByAccount = useMemo(() => {
        return Object.groupBy(LOCATIONS, l => l.accountName);
    }, [LOCATIONS]);

    return (
        <>
            <Header>
                <Typography variant="h6">{t('OnboardingPage.titleGoogle')}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', marginTop: '4px' }}>
                    {t('OnboardingPage.subtitleGoogle')}
                </Typography>
            </Header>
            <div style={{ gridArea: 'main', overflowY: 'auto' }}>
                {!locationsLoading ? (
                    <GoogleContentWrapper>
                        {Object.entries(companiesByAccount).map(([accountName, locations]) => (
                            <ExpandedSectionWrapper
                                key={accountName}
                                isOpen={section[accountName]}
                                onClick={() => setSection(prev => ({ ...prev, [accountName]: !prev[accountName] }))}
                                text={accountName}
                            >
                                {locations.map(l => (
                                    <GoogleCompanyItem
                                        key={l.name}
                                        location={l}
                                        selectedCompanies={companies}
                                        setCompany={onCheckCompany}
                                    />
                                ))}
                            </ExpandedSectionWrapper>
                        ))}
                        <>
                            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', marginTop: '16px' }}>
                                {t('OnboardingPage.canTryNext')}
                            </Typography>
                            <GoogleFooter>
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    onClick={onAddManuallyClick}
                                    disabled={isSubmitting}
                                >
                                    {t('buttons.addManually')}
                                </Button>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        margin: '0 12px',
                                        '@media (max-width: 600px)': {
                                            margin: '8px 0',
                                        },
                                    }}
                                >
                                    {t('common.or')}
                                </Typography>
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    onClick={onConnectAnotherAccountClick}
                                    disabled={isSubmitting}
                                >
                                    {t('buttons.connectAnotherAccount')}
                                </Button>
                            </GoogleFooter>
                        </>
                    </GoogleContentWrapper>
                ) : (
                    <Progress />
                )}
            </div>
            <Footer isSpaceBetween>
                <Button type="submit" variant="outlined" onClick={onCheckAll}>
                    {companies.length === LOCATIONS.length ? t('buttons.deselectAll') : t('buttons.selectAll')}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    onClick={onComplete}
                    disabled={companies.length === 0 || isSubmitting}
                >
                    {t('buttons.complete')}
                </Button>
            </Footer>
        </>
    );
};

export default ViewGoogle;
